/**
 * 路由级 RBAC 守卫：在 Loading 期间撑住骨架，未授权统一回退到 403 / 跳转。
 *
 * 用法：
 *   <RequirePermission code="menu:admin"><AdminPage /></RequirePermission>
 *   <RequireRole code="admin"><AdminPage /></RequireRole>
 *
 * 也可作为 react-router v7 的 element 包裹器（只读，不依赖 router context）。
 */
import { ReactNode } from 'react';
import { Button, Result, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/common/auth/adminAcl';

interface GuardProps {
  /** 任一权限码满足即放行；不传则忽略权限校验。 */
  permission?: string | string[];
  /** 任一角色码满足即放行；不传则忽略角色校验。 */
  role?: string | string[];
  /** 自定义未授权时的展示。默认 Antd Result 403。 */
  fallback?: ReactNode;
  children: ReactNode;
}

const toArray = (v?: string | string[]): string[] => (
  v == null ? [] : Array.isArray(v) ? v : [v]
);

/** 通用守卫：同时校验 ``permission`` 与 ``role``（取交集，OR 逻辑） */
export function RequireAuth({ permission, role, fallback, children }: GuardProps) {
  const navigate = useNavigate();
  const { loading, isAdmin, hasPerm, hasRole } = useCurrentUser();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin />
      </div>
    );
  }

  const perms = toArray(permission);
  const roles = toArray(role);
  // 任一条件满足放行；都不传则等价于"仅要求登录"，但中间件已保证登录。
  const passPerm = perms.length > 0 && perms.some((p) => hasPerm(p));
  const passRole = roles.length > 0 && roles.some((r) => hasRole(r));
  const passEmpty = perms.length === 0 && roles.length === 0;
  const ok = isAdmin || passPerm || passRole || passEmpty;

  if (!ok) {
    if (fallback) return <>{fallback}</>;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Result
          status="403"
          title="无权限访问"
          subTitle={`需要权限 ${perms.join(', ') || '-'}${roles.length ? ` / 角色 ${roles.join(', ')}` : ''}`}
          extra={(
            <Button type="primary" onClick={() => navigate('/', { replace: true })}>
              返回首页
            </Button>
          )}
        />
      </div>
    );
  }

  return <>{children}</>;
}

/** 语法糖：仅校验 permission（最常用）。 */
export function RequirePermission(
  { code, children, fallback }: { code: string | string[]; children: ReactNode; fallback?: ReactNode },
) {
  return <RequireAuth permission={code} fallback={fallback}>{children}</RequireAuth>;
}

/** 语法糖：仅校验 role。 */
export function RequireRole(
  { code, children, fallback }: { code: string | string[]; children: ReactNode; fallback?: ReactNode },
) {
  return <RequireAuth role={code} fallback={fallback}>{children}</RequireAuth>;
}

/** 按钮级守卫：直接渲染或返回 null（搭配 useCurrentUser().hasPerm 用更轻）。 */
export function CanI(
  { perm, role, children }: { perm?: string; role?: string; children: ReactNode },
) {
  const { isAdmin, hasPerm, hasRole } = useCurrentUser();
  if (isAdmin) return <>{children}</>;
  if (perm && hasPerm(perm)) return <>{children}</>;
  if (role && hasRole(role)) return <>{children}</>;
  return null;
}
